import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { postPublicChat } from './api';
import { adoptServerChatSessionId, clearPersistedChatSessionId, ensureChatSessionId } from './sessionId';
import {
  CHAT_NUDGE_DELAY_MS,
  NUDGE_CTA,
  NUDGE_SESSION_KEY,
  NUDGE_TEXT,
  ERROR_FALLBACK,
  INTRO_HEADLINE,
  INTRO_SUBLINE,
  MESSAGES_STORAGE_KEY,
  MIN_TYPING_MS,
  WELCOME_TEXT,
  calendlyEmbedUrl,
  DEMO_CALENDLY_URL,
} from './constants';
import type { ChatMessage } from './types';
import { MessageBubble } from './MessageBubble';
import { ChatInput } from './ChatInput';
import { TypingIndicator } from './TypingIndicator';
import { GeofleetChatMark } from './GeofleetChatMark';
import { ChatEmbedProvider } from './ChatEmbedContext';

function uid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function loadPersistedMessages(): ChatMessage[] | null {
  try {
    const raw = localStorage.getItem(MESSAGES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    const out: ChatMessage[] = [];
    for (const row of parsed) {
      if (!row || typeof row !== 'object') continue;
      const m = row as Partial<ChatMessage>;
      if (m.role !== 'user' && m.role !== 'assistant') continue;
      if (typeof m.content !== 'string' || typeof m.id !== 'string') continue;
      out.push({
        id: m.id,
        role: m.role,
        content: m.content,
        createdAt: typeof m.createdAt === 'number' ? m.createdAt : undefined,
        cta: m.cta ?? undefined,
        intent: typeof m.intent === 'string' ? m.intent : undefined,
        isError: Boolean(m.isError),
      });
    }
    return out.length > 0 ? out : null;
  } catch {
    return null;
  }
}

function persistMessages(messages: ChatMessage[]) {
  try {
    localStorage.setItem(MESSAGES_STORAGE_KEY, JSON.stringify(messages.slice(-60)));
  } catch {
    /* quota / private mode */
  }
}

const QUICK_ACTIONS = [
  { label: 'See Features' as const, message: 'What features does Geofleet offer?' },
  { label: 'Pricing' as const, message: 'How does Geofleet pricing work?' },
  {
    label: 'Book a Demo' as const,
    message: 'I would like to book a demo with Geofleet.',
    embedUrl: calendlyEmbedUrl(DEMO_CALENDLY_URL),
  },
];

function hasPersistedUserMessage(): boolean {
  const saved = loadPersistedMessages();
  return Boolean(saved?.some((m) => m.role === 'user'));
}

/**
 * Geofleet assistant — shows a small nudge bubble once per tab session after
 * {@link CHAT_NUDGE_DELAY_MS}; the panel only opens when the visitor asks. Same-tab repeat is
 * prevented via {@link NUDGE_SESSION_KEY}; opening the widget or dismissing the nudge marks it consumed.
 */
export default function GeofleetPublicChatWidget() {
  const panelId = useId();
  const listEndRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(false);
  const nudgeTimerRef = useRef<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);

  /** When sessionStorage is blocked, still remember "already handled" for this tab. */
  const autoOpenSessionFallbackRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  /** In-panel web view (e.g. Calendly) — replaces transcript area until dismissed. */
  const [embeddedUrl, setEmbeddedUrl] = useState<string | null>(null);
  /** “Start a conversation” + quick actions — hide after chat starts or any of those controls is used. */
  const [showStarterActions, setShowStarterActions] = useState(() => !hasPersistedUserMessage());
  const [input, setInput] = useState('');
  const [awaiting, setAwaiting] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const saved = loadPersistedMessages();
    if (saved) return saved;
    return [{ id: uid(), role: 'assistant', content: WELCOME_TEXT, createdAt: Date.now() }];
  });

  openRef.current = open;

  const openEmbeddedUrl = useCallback((url: string) => {
    const u = url.trim();
    if (!u) return;
    setShowStarterActions(false);
    setEmbeddedUrl(u);
  }, []);

  const closeEmbeddedUrl = useCallback(() => setEmbeddedUrl(null), []);

  const collapseStarterActions = useCallback(() => setShowStarterActions(false), []);

  const closePanel = useCallback(() => {
    setEmbeddedUrl(null);
    setOpen(false);
  }, []);

  const markAutoOpenConsumed = useCallback(() => {
    try {
      sessionStorage.setItem(NUDGE_SESSION_KEY, '1');
    } catch {
      autoOpenSessionFallbackRef.current = true;
    }
  }, []);

  const hasAutoOpenConsumed = useCallback(() => {
    if (autoOpenSessionFallbackRef.current) return true;
    try {
      return Boolean(sessionStorage.getItem(NUDGE_SESSION_KEY));
    } catch {
      return false;
    }
  }, []);

  /** Schedule one delayed nudge per tab session (never auto-opens the panel). */
  useEffect(() => {
    if (hasAutoOpenConsumed()) return;

    nudgeTimerRef.current = window.setTimeout(() => {
      nudgeTimerRef.current = null;
      if (openRef.current || hasAutoOpenConsumed()) return;
      setShowNudge(true);
    }, CHAT_NUDGE_DELAY_MS);

    return () => {
      if (nudgeTimerRef.current != null) {
        clearTimeout(nudgeTimerRef.current);
        nudgeTimerRef.current = null;
      }
    };
    // Mount only: the timeout reads live session/ref state via hasAutoOpenConsumed and openRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissNudge = useCallback(() => {
    setShowNudge(false);
    markAutoOpenConsumed();
  }, [markAutoOpenConsumed]);

  useEffect(() => {
    if (open) {
      if (nudgeTimerRef.current != null) {
        clearTimeout(nudgeTimerRef.current);
        nudgeTimerRef.current = null;
      }
      setShowNudge(false);
      markAutoOpenConsumed();
    } else {
      setHeaderMenuOpen(false);
    }
  }, [open, markAutoOpenConsumed]);

  useEffect(() => {
    persistMessages(messages);
  }, [messages]);

  useEffect(() => {
    if (!open) return;
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, awaiting, open]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      document.getElementById('geofleet-public-chat-input')?.focus();
    }, 200);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (headerMenuOpen) {
          setHeaderMenuOpen(false);
          return;
        }
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, headerMenuOpen]);

  useEffect(() => {
    if (!headerMenuOpen) return;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const el = menuRef.current;
      if (el && !el.contains(e.target as Node)) setHeaderMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [headerMenuOpen]);

  const clearChat = useCallback(() => {
    setMessages([{ id: uid(), role: 'assistant', content: WELCOME_TEXT, createdAt: Date.now() }]);
    setInput('');
    setHeaderMenuOpen(false);
    try {
      localStorage.removeItem(MESSAGES_STORAGE_KEY);
    } catch {
      /* private mode */
    }
    clearPersistedChatSessionId();
    setEmbeddedUrl(null);
    setShowStarterActions(true);
  }, []);

  const appendUser = useCallback((content: string) => {
    setMessages((prev) => [...prev, { id: uid(), role: 'user', content, createdAt: Date.now() }]);
  }, []);

  const appendAssistant = useCallback((msg: Omit<ChatMessage, 'id' | 'role' | 'createdAt'>) => {
    setMessages((prev) => [...prev, { id: uid(), role: 'assistant', createdAt: Date.now(), ...msg }]);
  }, []);

  const runAssistantTurn = useCallback(
    async (userText: string) => {
      const started = Date.now();
      setAwaiting(true);
      try {
        const sessionId = ensureChatSessionId();
        const data = await postPublicChat(userText, sessionId);
        if (typeof data.session_id === 'string' && data.session_id.trim()) {
          adoptServerChatSessionId(data.session_id.trim());
        }
        const elapsed = Date.now() - started;
        await new Promise((r) => setTimeout(r, Math.max(0, MIN_TYPING_MS - elapsed)));
        const replyText =
          typeof data.reply === 'string' && data.reply.trim() ? data.reply.trim() : ERROR_FALLBACK;
        appendAssistant({
          content: replyText,
          cta: data.cta ?? undefined,
          intent: typeof data.intent === 'string' ? data.intent : undefined,
        });
      } catch {
        const elapsed = Date.now() - started;
        await new Promise((r) => setTimeout(r, Math.max(0, MIN_TYPING_MS - elapsed)));
        appendAssistant({ content: ERROR_FALLBACK, isError: true });
      } finally {
        setAwaiting(false);
      }
    },
    [appendAssistant]
  );

  const handleSend = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text || awaiting) return;
      setShowStarterActions(false);
      appendUser(text);
      setInput('');
      await runAssistantTurn(text);
    },
    [appendUser, awaiting, runAssistantTurn]
  );

  const togglePanel = useCallback(() => {
    setOpen((o) => !o);
  }, []);

  const focusComposer = useCallback(() => {
    collapseStarterActions();
    window.requestAnimationFrame(() => {
      document.getElementById('geofleet-public-chat-input')?.focus();
    });
  }, [collapseStarterActions]);

  return (
    <div className="pointer-events-none fixed bottom-0 right-0 z-[2147483000] flex flex-col items-end gap-2 px-3 pb-1.5 pt-6 sm:gap-2 sm:px-4 sm:pb-2 sm:pt-7 font-sans antialiased">
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            id={panelId}
            role="dialog"
            aria-modal="true"
            aria-label="Geofleet Assistant"
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.98 }}
            transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-auto flex h-[min(100dvh-6.5rem,600px)] w-[min(100vw-1.5rem,360px)] flex-col overflow-hidden rounded-[26px] bg-white shadow-[0_18px_56px_-16px_rgba(0,0,0,0.18)] ring-1 ring-black/[0.04]"
          >
            <ChatEmbedProvider openEmbeddedUrl={openEmbeddedUrl}>
            <header className="shrink-0 px-4 pb-2 pt-5 sm:px-5 sm:pt-6">
              <div className="flex items-start gap-2.5 sm:gap-3">
                <GeofleetChatMark size={34} circle className="shadow-sm" />
                <div className="min-w-0 flex-1 pt-0.5">
                  <h2 className="whitespace-nowrap text-[13px] font-semibold leading-tight tracking-tight text-neutral-950 sm:text-[14px]">
                    {INTRO_HEADLINE}
                  </h2>
                  <p className="mt-1 text-[12px] leading-snug text-neutral-500">{INTRO_SUBLINE}</p>
                </div>
                <div className="relative flex shrink-0 items-center gap-0" ref={menuRef}>
                  <button
                    type="button"
                    onClick={() => setHeaderMenuOpen((v) => !v)}
                    className={`flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-800 ${headerMenuOpen ? 'bg-neutral-100 text-neutral-900' : ''}`}
                    aria-expanded={headerMenuOpen}
                    aria-haspopup="menu"
                    aria-label="More options"
                  >
                    <i className="ri-more-2-fill text-lg" aria-hidden />
                  </button>
                  {headerMenuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 top-[calc(100%+6px)] z-20 min-w-[12rem] overflow-hidden rounded-2xl border border-neutral-200/90 bg-white py-1 shadow-xl shadow-neutral-900/10"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        disabled={awaiting}
                        onClick={clearChat}
                        className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm font-medium text-neutral-800 transition hover:bg-neutral-50 disabled:opacity-50"
                      >
                        <i className="ri-delete-bin-6-line text-base text-neutral-400" aria-hidden />
                        Clear chat
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={closePanel}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-800"
                    aria-label="Close chat"
                  >
                    <i className="ri-close-line text-lg" aria-hidden />
                  </button>
                </div>
              </div>
            </header>

            {embeddedUrl ? (
              <div className="relative mx-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-[20px] bg-white ring-1 ring-neutral-900/[0.06] sm:mx-3.5">
                <div className="flex shrink-0 items-center gap-1.5 border-b border-neutral-100 px-2 py-1.5 sm:px-2.5">
                  <button
                    type="button"
                    onClick={closeEmbeddedUrl}
                    className="flex h-8 shrink-0 items-center gap-1 rounded-full px-2 text-xs font-medium text-neutral-700 transition hover:bg-neutral-100 sm:text-[13px]"
                  >
                    <i className="ri-arrow-left-line text-base" aria-hidden />
                    Chat
                  </button>
                  <span className="min-w-0 flex-1 truncate text-center text-xs font-semibold text-neutral-900 sm:text-[13px]">
                    Book a demo
                  </span>
                  <span className="w-12 shrink-0 sm:w-14" aria-hidden />
                </div>
                <iframe
                  src={embeddedUrl}
                  title="Book a demo — scheduling"
                  className="min-h-0 w-full flex-1 border-0 bg-white"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            ) : (
              <div className="relative mx-3 min-h-0 flex-1 overflow-hidden rounded-[20px] bg-neutral-50/90 ring-1 ring-neutral-900/[0.06] sm:mx-3.5">
                <div className="h-full overflow-y-auto overscroll-contain px-3 pb-2 pt-5 sm:px-3.5 sm:pt-6">
                  <div className="space-y-3">
                    {messages.map((m) => (
                      <MessageBubble key={m.id} message={m} />
                    ))}
                    {awaiting && (
                      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
                        <TypingIndicator />
                      </motion.div>
                    )}
                    <div ref={listEndRef} />
                  </div>
                </div>
              </div>
            )}

            <footer className="shrink-0 space-y-2.5 bg-white px-4 pb-2.5 pt-3 sm:px-5 sm:pb-3 sm:pt-3.5">
              <AnimatePresence initial={false}>
                {showStarterActions && (
                  <motion.div
                    key="starter-actions"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    className="space-y-2.5"
                  >
                    <button
                      type="button"
                      disabled={awaiting}
                      onClick={focusComposer}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-neutral-950 py-2.5 text-[13px] font-medium text-white shadow-md shadow-neutral-900/15 transition hover:bg-neutral-800 active:scale-[0.99] disabled:pointer-events-none disabled:opacity-45 sm:py-3 sm:text-[14px]"
                    >
                      <i className="ri-chat-3-line text-base sm:text-lg" aria-hidden />
                      Start a conversation
                    </button>
                    <div className="flex flex-col gap-1.5">
                      {QUICK_ACTIONS.map((a) => (
                        <button
                          key={a.label}
                          type="button"
                          disabled={awaiting}
                          onClick={() => {
                            if ('embedUrl' in a && a.embedUrl) {
                              openEmbeddedUrl(a.embedUrl);
                              return;
                            }
                            void handleSend(a.message);
                          }}
                          className="flex w-full items-center justify-center gap-1.5 rounded-full border border-neutral-200 bg-white py-2.5 text-[13px] font-medium text-neutral-900 transition hover:border-neutral-300 hover:bg-neutral-50 disabled:opacity-45 sm:gap-2 sm:py-2.5 sm:text-[13.5px]"
                        >
                          {a.label === 'Book a Demo' ? <i className="ri-calendar-line text-sm text-neutral-600 sm:text-base" aria-hidden /> : null}
                          {a.label === 'Pricing' ? <i className="ri-price-tag-3-line text-sm text-neutral-600 sm:text-base" aria-hidden /> : null}
                          {a.label === 'See Features' ? <i className="ri-sparkling-line text-sm text-neutral-600 sm:text-base" aria-hidden /> : null}
                          {a.label}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <ChatInput
                value={input}
                onChange={setInput}
                disabled={awaiting}
                onSubmit={() => void handleSend(input)}
                placeholder="Type your message…"
              />
              <p className="text-center text-[10px] leading-snug text-neutral-400 sm:text-[11px]">
                By chatting, you agree to our{' '}
                <Link to="/privacy" className="font-medium text-neutral-600 underline underline-offset-2 hover:text-neutral-950">
                  Privacy Policy
                </Link>
                .
              </p>
            </footer>
            </ChatEmbedProvider>
          </motion.div>
        )}
      </AnimatePresence>

      {open && (
        <p className="pointer-events-none pr-0.5 text-[9px] font-medium tracking-wide text-neutral-400 sm:text-[10px]">
          Powered by Geofleet AI
        </p>
      )}

      <AnimatePresence>
        {open && (
          <motion.button
            key="collapse"
            type="button"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.18 }}
            onClick={closePanel}
            className="pointer-events-auto flex h-9 w-9 items-center justify-center rounded-full bg-neutral-950 text-white shadow-lg shadow-neutral-900/20 ring-[3px] ring-black/[0.06] sm:h-10 sm:w-10"
            aria-label="Minimize chat"
          >
            <i className="ri-arrow-down-s-line text-base sm:text-lg" aria-hidden />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!open && showNudge && (
          <motion.div
            key="nudge"
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 380, damping: 28 }}
            className="pointer-events-auto relative hidden max-w-[260px] items-start gap-1 rounded-2xl sm:flex border border-neutral-200/90 bg-white py-2.5 pl-3.5 pr-1.5 shadow-[0_12px_32px_-10px_rgba(15,23,42,0.22)]"
          >
            <button type="button" onClick={togglePanel} className="text-left" aria-controls={panelId}>
              <span className="block text-[13px] leading-snug text-neutral-700">{NUDGE_TEXT}</span>
              <span className="mt-0.5 inline-flex items-center gap-1 text-[13px] font-semibold text-neutral-950">
                {NUDGE_CTA}
                <i className="ri-arrow-right-line" aria-hidden />
              </span>
            </button>
            <button
              type="button"
              onClick={dismissNudge}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-700"
              aria-label="Dismiss"
            >
              <i className="ri-close-line text-base" aria-hidden />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {!open && (
        <motion.button
          type="button"
          layout
          onClick={togglePanel}
          aria-expanded={false}
          aria-controls={panelId}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="pointer-events-auto flex items-center gap-2 rounded-[22px] border border-neutral-200/80 bg-white py-2 pl-2.5 pr-3.5 shadow-[0_10px_32px_-8px_rgba(0,0,0,0.14)] ring-0 transition-shadow hover:shadow-[0_14px_36px_-10px_rgba(0,0,0,0.18)] max-sm:p-1.5 sm:gap-2.5 sm:rounded-[24px] sm:py-2 sm:pl-3 sm:pr-4"
        >
          <GeofleetChatMark size={30} circle />
          <span className="hidden text-[13px] font-semibold tracking-tight text-neutral-950 sm:inline sm:text-[14px]">Need help?</span>
          <span className="sr-only">Open Geofleet Assistant</span>
        </motion.button>
      )}
    </div>
  );
}
